/**
 * Onboarding Sequence Templates
 * Based on best practices from HubSpot, Intercom, Slack, Dropbox
 *
 * Key insights from billion-dollar companies:
 * - First email within 5 minutes of signup (immediate value)
 * - 5-7 emails over 14 days is optimal
 * - Focus on ONE action per email
 * - Day 1-3: Activation (get them using AI copilots)
 * - Day 4-7: Engagement (show advanced features)
 * - Day 8-14: Retention (social proof, success stories)
 */

import { SequenceTemplate } from "./types";

export const ONBOARDING_5_DAY: SequenceTemplate = {
  id: "onboarding-5-day",
  name: "5-Day Onboarding Journey",
  type: "onboarding",
  description: "Guide new users from signup to AI copilot success in 5 strategic touchpoints",
  strategy: "This sequence follows the activation-engagement-retention framework used by top SaaS companies. Each email has ONE clear goal and builds on the previous one.",
  icon: "RocketOutlined",
  color: "#0ea5e9",
  expectedOutcomes: [
    "40-60% email open rate",
    "25-35% click-through rate",
    "50%+ activation within 7 days",
    "Reduced churn in first 30 days"
  ],
  bestPractices: [
    "Send first email immediately - users are most engaged right after signup",
    "Each email should have ONE clear call-to-action",
    "Use the user's name and personalize based on their actions",
    "Track which emails drive the most activations and optimize"
  ],
  trigger: {
    eventType: "signup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Day 0 - Welcome & Quick Win",
      subject: "Welcome to {{product_name}}! Let's get you started",
      suggestedTemplateId: "onboarding-day1",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Immediate welcome + guide to first AI copilot session",
      variables: [
        { key: "product_name", defaultValue: "Copilotly" },
        { key: "first_name", defaultValue: "there" },
        { key: "dashboard_url", defaultValue: "{{dashboard_url}}" }
      ]
    },
    {
      name: "Day 2 - Check-in & Feature Highlight",
      subject: "{{first_name}}, have you tried this yet?",
      suggestedTemplateId: "onboarding-day3",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Check if they've made first generation, highlight key copilots",
      variables: [
        { key: "feature_name", defaultValue: "Legal Copilot" },
        { key: "feature_benefit", defaultValue: "Draft legal documents in seconds" }
      ]
    },
    {
      name: "Day 4 - Pro Tips",
      subject: "3 tips to get more from your AI copilots",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Share power-user tips to increase engagement",
      variables: [
        { key: "tip_1", defaultValue: "Try different AI models (GPT-4, Claude, Gemini)" },
        { key: "tip_2", defaultValue: "Install the browser extension for instant access" },
        { key: "tip_3", defaultValue: "Explore profession-specific copilots" }
      ]
    },
    {
      name: "Day 7 - Success Story",
      subject: "How {{company_name}} saves 10+ hours weekly",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Social proof - show what's possible with real results",
      variables: [
        { key: "company_name", defaultValue: "TechCorp" },
        { key: "result_metric", defaultValue: "10+ hours saved weekly" }
      ]
    },
    {
      name: "Day 14 - Advanced Features",
      subject: "Ready for the next level? Unlock advanced features",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Introduce premium copilots or upgrade path",
      variables: [
        { key: "advanced_feature", defaultValue: "Premium AI models" },
        { key: "upgrade_benefit", defaultValue: "Unlimited generations" }
      ]
    }
  ],
  tags: ["onboarding", "welcome", "activation", "new-user", "5-day"],
  bestFor: ["SaaS products", "Self-service platforms", "Freemium models"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const ONBOARDING_QUICK_START: SequenceTemplate = {
  id: "onboarding-quick-start",
  name: "Quick Start (3-Email)",
  type: "onboarding",
  description: "Condensed onboarding for users who prefer minimal emails",
  strategy: "A streamlined 3-email sequence that delivers maximum value with minimum friction. Perfect for technical audiences who don't need hand-holding.",
  icon: "ThunderboltOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "Higher open rates due to less email fatigue",
    "35%+ activation rate",
    "Lower unsubscribe rate"
  ],
  bestPractices: [
    "Pack more value into each email",
    "Include video tutorials for visual learners",
    "Make unsubscribe easy - quality over quantity"
  ],
  trigger: {
    eventType: "signup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Immediate - Welcome & Setup Guide",
      subject: "Your {{product_name}} account is ready!",
      suggestedTemplateId: "welcome",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Welcome + comprehensive getting started guide"
    },
    {
      name: "Day 3 - Value Unlock",
      subject: "{{first_name}}, you're missing out on this...",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Highlight unused copilots + quick wins"
    },
    {
      name: "Day 7 - Success Check",
      subject: "How's it going? Here's what successful users do",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Check-in + best practices from power users"
    }
  ],
  tags: ["onboarding", "quick", "minimal", "3-email"],
  bestFor: ["Technical products", "Developer tools", "B2B SaaS"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const ONBOARDING_HIGH_TOUCH: SequenceTemplate = {
  id: "onboarding-high-touch",
  name: "High-Touch Enterprise Onboarding",
  type: "onboarding",
  description: "Comprehensive 7-email sequence for enterprise customers",
  strategy: "A white-glove onboarding experience with personalized touches, dedicated support offers, and success manager introductions. Used by companies like Salesforce and HubSpot.",
  icon: "CrownOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "70%+ email engagement",
    "Higher NPS scores",
    "Faster time-to-value",
    "Stronger customer relationships"
  ],
  bestPractices: [
    "Include personal video from success manager",
    "Offer 1:1 onboarding calls",
    "Send from a real person, not generic email",
    "Track individual engagement and follow up personally"
  ],
  trigger: {
    eventType: "signup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: ["Wizard", "Maverick"],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Immediate - Personal Welcome",
      subject: "Welcome {{first_name}}! I'm your dedicated success manager",
      suggestedTemplateId: "onboarding-day1",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Personal introduction from success manager"
    },
    {
      name: "Day 1 - Setup Assistance",
      subject: "Let me help you explore your AI copilots",
      suggestedTemplateId: "onboarding-day3",
      delayAfterPrevious: { value: 1, unit: "days" },
      purpose: "Offer hands-on setup assistance"
    },
    {
      name: "Day 3 - Training Resources",
      subject: "Your exclusive training materials are ready",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Share premium training resources"
    },
    {
      name: "Day 5 - Team Setup Help",
      subject: "Let's get your team set up with {{product_name}}",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Help with team invites and workspace setup"
    },
    {
      name: "Day 7 - Check-in Call Invite",
      subject: "Quick call this week, {{first_name}}?",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Schedule first success check-in call"
    },
    {
      name: "Day 10 - Advanced Training",
      subject: "Unlock enterprise features with this guide",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Introduce enterprise-specific copilots and features"
    },
    {
      name: "Day 14 - Success Review",
      subject: "Your first 2 weeks: Let's review your progress",
      suggestedTemplateId: "weekly-digest",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Review progress and plan next steps"
    }
  ],
  tags: ["onboarding", "enterprise", "high-touch", "premium", "7-email"],
  bestFor: ["Enterprise SaaS", "High-ticket products", "Complex platforms"],
  difficulty: "advanced",
  setupTime: "30 minutes"
};

export const ONBOARDING_TEMPLATES = [
  ONBOARDING_5_DAY,
  ONBOARDING_QUICK_START,
  ONBOARDING_HIGH_TOUCH
];
